import {
  AnimationUtils,
  Direction,
} from '@/game/GameComponents/PhaserGame/Utils/AnimationUtils';
import TilemapScene from '@/game/scenes/TilemapScenes/TilemapScene';
import Vector2 = Phaser.Math.Vector2;

/* START OF COMPILED CODE */

import NPC from './NPC';
/* START-USER-IMPORTS */
import { uiAssetsPath } from '@/app/constants';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import DynamicSortByY from '@/game/GameComponents/PhaserGame/Utils/DynamicSortByY';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { waitForMilliseconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { HOVER_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import _ from 'lodash';

/* END-USER-IMPORTS */

export default class WorldNode extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    // nPC
    const nPC = new NPC(scene, 0, 0);
    nPC.removeInteractive();
    nPC.setInteractive(
      new Phaser.Geom.Rectangle(-32, -32, 64, 65),
      Phaser.Geom.Rectangle.Contains,
    );
    nPC.scaleX = 1;
    nPC.scaleY = 1;
    this.add(nPC);

    // statusBubble
    const statusBubble = scene.add.image(0, -32, 'remaining');
    statusBubble.setOrigin(0.5, 1);
    this.add(statusBubble);

    // npcStats
    const npcStats = scene.add.container(0, -70);
    this.add(npcStats);

    // npcID
    const npcID = scene.add.bitmapText(0, 0, 'Pixellari', '99');
    npcID.setOrigin(0.5, 0.5);
    npcID.text = '99';
    npcID.fontSize = -16;
    npcID.align = 1;
    npcStats.add(npcID);

    this.nPC = nPC;
    this.statusBubble = statusBubble;
    this.npcID = npcID;
    this.npcStats = npcStats;

    /* START-USER-CTR-CODE */

    // Record this node in the tilemap scene.
    const tilemapScene = this.scene as TilemapScene;
    if (tilemapScene) {
      tilemapScene.nodes.push(this);
    }

    new DynamicSortByY(this, this.scene);

    /* END-USER-CTR-CODE */
  }

  public nPC: NPC;
  private statusBubble: Phaser.GameObjects.Image;
  private npcID: Phaser.GameObjects.BitmapText;
  private npcStats: Phaser.GameObjects.Container;
  public NPCLevelId: number = 0;
  public NPCId: number = 0;

  /* START-USER-CODE */

  index?: number;
  behavior?: string;
  distance: number = 0;
  duration: number = 0;
  nodeObj?: Phaser.GameObjects.Image;
  remainingNode?: Phaser.GameObjects.Image;
  currentNode?: Phaser.GameObjects.Image;
  completedNode?: Phaser.GameObjects.Image;
  npcStatus?: NodeStatus;
  tooltip?: Phaser.GameObjects.Text; //adding for dev purposes
  callback?: (node: WorldNode) => void;
  gameStartedCallback?: (node: WorldNode) => void;
  tooltipOffset: Vector2 = Vector2.ZERO;
  hasInitializedAnimations: boolean = false;

  static debugWorldNodes: boolean = false;

  initialize({
    index,
    behavior,
    distance,
    duration,
    callback,
    gameStartedCallback,
  }: WorldNodeProps) {
    this.index = index;
    this.callback = callback;
    this.gameStartedCallback = gameStartedCallback;
    this.behavior = behavior;
    this.distance = distance;
    this.duration = duration;

    const { nPC } = this;
    nPC.npc_complexion.setInteractive({ cursor: `${HOVER_CURSOR_PATH}, auto` });
    nPC.npc_complexion.on('pointerdown', () => this.callback!(this));
    nPC.npc_complexion.on('pointerout', () => {
      this.scene.input.setDefaultCursor(
        `url(${uiAssetsPath}cursors/glove_pointer_64_64.png), auto`,
      );
    });

    // Initialize the NPC with the correct properties.
    this.nPC.npcLevelId = this.NPCLevelId;
    this.nPC.npcId = this.NPCId;
    this.nodeObj = this.remainingNode;

    // Tooltip initialization
    this.tooltipOffset = new Vector2(this.statusBubble.x, this.statusBubble.y);
    this.scene.events.on('prerender', this.handlePrerender, this);
    EventsManager.registerEventOnce(
      this.scene,
      GAME_EVENTS.FINISHED_NPC_ASSET_PRELOAD,
      this.handleNPCAssetPreload,
      this,
    );

    // Useful information for debugging.
    this.npcStats.setVisible(
      process.env.NODE_ENV === 'development' && WorldNode.debugWorldNodes,
    );
    this.npcID.setText(`${this.NPCLevelId}-${this.NPCId}`);
  }

  setStatus = (npcStatus: NodeStatus) => {
    if (this.npcStatus === npcStatus) {
      return;
    }
    this.npcStatus = npcStatus;
    const bubbleTex =
      this.npcStatus === NodeStatus.COMPLETE ? 'complete' : 'remaining';
    this.statusBubble.setTexture(bubbleTex);
    // Update NPC based on the new status.
    switch (npcStatus) {
      case NodeStatus.COMPLETE:
        // Make the NPC do their satisfied animation.
        AnimationUtils.setSatisfied(this.nPC);
        break;
      case NodeStatus.REMAINING:
        // Make the NPC do their unsatisfied animation.
        this.initializeAnimations();
        break;
    }
  };

  public initializeAnimations() {
    const { distance, duration, behavior } = this;
    if (behavior === 'pace') {
      AnimationUtils.paceBackAndForth(this.nPC, this.scene, distance, duration);
    } else {
      AnimationUtils.setGenericAnimation(
        this.nPC,
        this.scene,
        behavior || 'idle',
      );
    }
    this.startIdleAnimationOneShot();
  }

  hideTooltip = () => {
    this.statusBubble.setVisible(false);
  };

  setIdle(dir: Direction) {
    // The NPCs idle animation will differ depending on its state.
    switch (this.npcStatus) {
      case NodeStatus.COMPLETE:
        AnimationUtils.setSatisfied(this.nPC);
        break;
      case NodeStatus.REMAINING:
        AnimationUtils.setIdle(this, dir);
        break;
    }
  }

  // Plays a random idle one shot on the NPC every 5-10 seconds.
  startIdleAnimationOneShot() {
    const countdown = Math.random() * 5000 + 5000;
    if (this.npcStatus === NodeStatus.REMAINING && this.behavior === 'idle') {
      // start the timeout.
      this.scene.time.delayedCall(countdown, async () => {
        if (
          this.npcStatus !== NodeStatus.REMAINING ||
          this.behavior !== 'idle'
        ) {
          return;
        }
        const chosenAnim = _.sample([
          'tug-collar',
          'shifty-eyes',
          'blink-turn-head',
        ]);
        AnimationUtils.setGenericAnimation(this.nPC, this.scene, chosenAnim);
        const complexion = this.nPC.npc_complexion;
        await waitForMilliseconds(
          this.scene.time,
          complexion.anims?.currentAnim?.duration ?? 0,
        );
        AnimationUtils.setGenericAnimation(this.nPC, this.scene, 'idle');
        this.startIdleAnimationOneShot();
      });
    }
  }

  public static getStatusImage(status: NodeStatus): string {
    switch (status) {
      case NodeStatus.REMAINING:
        return 'node_remaining';
      case NodeStatus.COMPLETE:
        return 'node_completed';
      default:
        return 'node_unknown';
    }
  }

  handlePrerender() {
    // Update status bubble position.
    const bubblePos = new Vector2(
      this.nPC?.x + this.tooltipOffset.x,
      this.nPC.y + this.tooltipOffset.y,
    );
    this.statusBubble.setPosition(bubblePos.x, bubblePos.y);
  }

  handleNodeClicked() {
    // Only face the player if our node hasn't been completed.
    if (this.npcStatus === NodeStatus.REMAINING) {
      this.setIdle(Direction.DOWN);
    }
  }

  handlePlayerReachedNode(playerBody: any) {
    const { nPC } = this;
    nPC?.setIdleFacingPlayer(playerBody);
    this.scene.tweens.killTweensOf(nPC);
  }

  handleModalClosed(node: WorldNode) {
    if (node === this) {
      // Animation.
      this.setIdle(Direction.DOWN);
    }
  }

  handleNPCAssetPreload() {
    this.setIdle(Direction.DOWN);
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

type WorldNodeProps = {
  index: number;
  behavior: string;
  distance: number;
  duration: number;
  callback: (e: any) => void;
  gameStartedCallback: (e: any) => void;
  npcStatus?: NodeStatus;
};

export enum NodeStatus {
  REMAINING,
  COMPLETE,
}
